import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FollowupItem, SearchHit } from '@shared/types';
import type { FollowupInit } from '@/lib/actions';
import { api, ApiError, errorMessage } from '@/lib/api';
import { useDebounced, useMe, useTeam } from '@/lib/hooks';
import { openDialog } from '@/lib/overlays';
import { queryClient } from '@/lib/queryClient';
import { toast, toastError } from '@/lib/toast';
import { Avatar, CompanyMono } from '@/ui/Avatar';
import { Spinner } from '@/ui/bits';
import { DialogBody, DialogCancel, DialogFoot, DialogHead } from '@/ui/Dialog';
import { dueBody, startDue, type Due } from '@/ui/due';
import { DueInput } from '@/ui/DueInput';
import { Field, InputField, SearchInput, SelectField } from '@/ui/Field';
import { Icon } from '@/ui/Icon';
import { binItems, fetchLinks, TZ } from './api';

// "Add a follow-up": what needs doing, when, whose it is, and the person, company or deal it is about.

type LinkType = 'person' | 'company' | 'deal';
interface Link {
  type: LinkType;
  id: string;
  name: string;
  hue: number;
  /** Companies: their logo. */
  logo?: string | null;
}
type Links = Partial<Record<LinkType, Link>>;

interface SearchResults {
  people: SearchHit[];
  companies: SearchHit[];
  deals: SearchHit[];
}

const TYPE_LABEL: Record<LinkType, string> = { person: 'Person', company: 'Company', deal: 'Deal' };

export function openFollowupDialog(init: FollowupInit = {}): void {
  openDialog({ label: 'Add a follow-up', render: (close) => <FollowupForm init={init} close={close} /> });
}

function FollowupForm({ init, close }: { init: FollowupInit; close: () => void }) {
  const me = useMe();
  const team = useTeam();

  const [title, setTitle] = useState(init.title ?? '');
  const [due, setDue] = useState<Due>(() => startDue(new Date(), TZ, 'tomorrow'));
  const [ownerId, setOwnerId] = useState(me.user.id);
  const [links, setLinks] = useState<Links>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const touched = useRef(false);
  const ids = useId();

  // Names for the records the dialog was opened from.
  const hasInit = Boolean(init.personId || init.companyId || init.dealId);
  const prefill = useQuery({
    queryKey: ['home', 'links', init.personId ?? '', init.companyId ?? '', init.dealId ?? ''],
    queryFn: () => fetchLinks({ personId: init.personId, companyId: init.companyId, dealId: init.dealId }),
    enabled: hasInit,
    staleTime: 60_000,
  });
  useEffect(() => {
    const d = prefill.data;
    if (!d || touched.current) return;
    const next: Links = {};
    if (d.person) next.person = { type: 'person', id: d.person.id, name: d.person.name, hue: d.person.hue };
    if (d.company) next.company = { type: 'company', id: d.company.id, name: d.company.name, hue: d.company.hue, logo: d.company.logo };
    if (d.deal) next.deal = { type: 'deal', id: d.deal.id, name: d.deal.company ? `${d.deal.company} · ${d.deal.name}` : d.deal.name, hue: d.deal.hue };
    setLinks(next);
  }, [prefill.data]);

  const owners = team.active.length ? team.active : [me.user];
  const ownerOptions = owners.map((m) => ({ value: m.id, label: m.id === me.user.id ? `${m.name} (you)` : m.name }));

  const setLink = (link: Link) => {
    touched.current = true;
    setLinks((l) => ({ ...l, [link.type]: link }));
  };
  const removeLink = (type: LinkType) => {
    touched.current = true;
    setLinks((l) => {
      const next = { ...l };
      delete next[type];
      return next;
    });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const local: Record<string, string> = {};
    if (!title.trim()) local.title = 'Describe the follow-up';
    else if (title.trim().length > 300) local.title = 'Keep it under 300 characters';
    if (!due.date) local.dueAt = 'Choose a date';
    setErrors(local);
    setFormError(null);
    if (Object.keys(local).length) return;
    setSaving(true);
    try {
      const item = await api.post<FollowupItem>('/followups', {
        title: title.trim(),
        ...dueBody(due, TZ),
        ownerId,
        personId: links.person?.id ?? null,
        companyId: links.company?.id ?? null,
        dealId: links.deal?.id ?? null,
      });
      void queryClient.invalidateQueries({ queryKey: ['home'] });
      void queryClient.invalidateQueries({ queryKey: ['followups'] });
      close();
      const owner = owners.find((m) => m.id === ownerId);
      toast(owner && owner.id !== me.user.id ? `Follow-up added for ${owner.name.split(' ')[0]}` : 'Follow-up added', {
        icon: 'clock',
        undo: () => {
          binItems('followup', [item.id])
            .then(() => {
              void queryClient.invalidateQueries({ queryKey: ['home'] });
              void queryClient.invalidateQueries({ queryKey: ['followups'] });
            })
            .catch((err) => toastError(err));
        },
      });
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length) {
        setErrors(err.fields);
        const other = Object.keys(err.fields).filter((k) => !['title', 'dueAt', 'ownerId'].includes(k));
        if (other.length) setFormError(err.fields[other[0]!] ?? err.message);
      } else {
        setFormError(errorMessage(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const dateError = errors.dueAt ?? errors.hasTime ?? null;

  return (
    <form onSubmit={(e) => void submit(e)} noValidate className="fu-dialog">
      <DialogHead title="Add a follow-up" sub="A reminder that shows on Home for whoever owns it." onClose={close} />
      <DialogBody>
        <InputField
          label="What needs doing"
          required
          autoFocus
          placeholder="Send the revised proposal to Lena"
          value={title}
          maxLength={300}
          error={errors.title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (errors.title) setErrors(({ title: _t, ...rest }) => rest);
          }}
        />

        <Field label="Due" required error={dateError} help="Leave the time empty for the whole day." htmlFor={`${ids}-date`} className="fud-sec">
          <DueInput
            id={`${ids}-date`}
            value={due}
            invalid={!!dateError}
            describedBy={dateError ? `${ids}-date-err` : `${ids}-date-help`}
            onChange={(next) => {
              setDue(next);
              if (errors.dueAt) setErrors(({ dueAt: _d, ...rest }) => rest);
            }}
          />
        </Field>

        <SelectField label="Owner" className="fud-sec" options={ownerOptions} value={ownerId} error={errors.ownerId} onChange={(e) => setOwnerId(e.target.value)} />

        <Field
          label="About"
          className="fud-sec"
          help={errors.personId ?? errors.companyId ?? errors.dealId ? undefined : 'It opens from the follow-up on Home.'}
          error={errors.personId ?? errors.companyId ?? errors.dealId ?? null}
          htmlFor={`${ids}-about`}
        >
          {hasInit && prefill.isLoading ? (
            <div className="fud-links">
              <Spinner label="Loading the linked records" />
            </div>
          ) : Object.keys(links).length ? (
            <div className="fud-links" aria-label="Linked records">
              {(['person', 'company', 'deal'] as const).map((t) => {
                const l = links[t];
                if (!l) return null;
                return (
                  <span className="rcpt" key={t}>
                    {t === 'person' ? <Avatar name={l.name} hue={l.hue} size="xs" /> : t === 'company' ? <CompanyMono name={l.name} hue={l.hue} logo={l.logo} size="xs" /> : <span className="fud-ic"><Icon name="deals" /></span>}
                    <span className="fud-ln">
                      <small>{TYPE_LABEL[t]}</small>
                      {l.name}
                    </span>
                    <button type="button" className="fud-x" aria-label={`Remove ${l.name}`} onClick={() => removeLink(t)}>
                      <Icon name="x" />
                    </button>
                  </span>
                );
              })}
            </div>
          ) : null}
          <LinkPicker id={`${ids}-about`} onPick={setLink} />
        </Field>
      </DialogBody>
      <DialogFoot>
        {formError ? (
          <span className="note err" role="alert">
            <Icon name="warn" />
            {formError}
          </span>
        ) : null}
        <span className="spacer" />
        <DialogCancel onClick={close} />
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? <span className="spin" aria-hidden="true" /> : <Icon name="plus" />}
          Add follow-up
        </button>
      </DialogFoot>
    </form>
  );
}

/** Search for a person, company or deal to link. */
function LinkPicker({ id, onPick }: { id: string; onPick: (link: Link) => void }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const term = useDebounced(q.trim(), 160);
  const results = useQuery({
    queryKey: ['search', 'followup-link', term],
    queryFn: ({ signal }) => api.get<SearchResults>('/search', { query: { q: term, limit: 5 }, signal }),
    enabled: term.length > 0,
    staleTime: 30_000,
  });
  const hits: Link[] = useMemo(() => {
    const d = results.data;
    if (!d) return [];
    return [
      ...d.people.map((h) => ({ type: 'person' as const, id: h.id, name: h.title, hue: h.hue, sub: h.subtitle })),
      ...d.companies.map((h) => ({ type: 'company' as const, id: h.id, name: h.title, hue: h.hue, logo: h.logo, sub: h.subtitle })),
      ...d.deals.map((h) => ({ type: 'deal' as const, id: h.id, name: h.title, hue: h.hue, sub: h.subtitle })),
    ];
  }, [results.data]);
  useEffect(() => setActive(0), [hits]);

  const pick = (h: Link) => {
    onPick({ type: h.type, id: h.id, name: h.name, hue: h.hue, logo: h.logo });
    setQ('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!hits.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i + (e.key === 'ArrowDown' ? 1 : hits.length - 1)) % hits.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const h = hits[active];
      if (h) pick(h);
    }
  };

  const listId = `${id}-results`;
  const showing = q.trim().length > 0;
  return (
    <div className="fud-pick">
      <SearchInput
        id={id}
        label="Search people, companies and deals"
        placeholder="Search people, companies and deals"
        value={q}
        autoComplete="off"
        role="combobox"
        aria-expanded={showing && hits.length > 0}
        aria-controls={listId}
        aria-activedescendant={showing && hits[active] ? `${listId}-${active}` : undefined}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={onKeyDown}
      />
      {showing ? (
        <div className="fud-results" id={listId} role="listbox" aria-label="Matching records">
          {term !== q.trim() || results.isFetching ? (
            hits.length ? null : (
              <div className="fud-note">
                <Spinner label="Searching" />
              </div>
            )
          ) : results.isError ? (
            <div className="fud-note err">{errorMessage(results.error)}</div>
          ) : hits.length === 0 ? (
            <div className="fud-note">Nothing matches “{q.trim()}”.</div>
          ) : null}
          {hits.map((h, i) => (
            <button
              key={`${h.type}-${h.id}`}
              id={`${listId}-${i}`}
              type="button"
              role="option"
              aria-selected={i === active}
              className={['pal-it', i === active ? 'on' : ''].filter(Boolean).join(' ')}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(h)}
            >
              {h.type === 'person' ? (
                <Avatar name={h.name} hue={h.hue} size="sm" />
              ) : h.type === 'company' ? (
                <CompanyMono name={h.name} hue={h.hue} logo={h.logo} size="sm" />
              ) : (
                <span className="ic">
                  <Icon name="deals" />
                </span>
              )}
              <span className="grow">
                <b>{h.name}</b>
                {(h as Link & { sub?: string | null }).sub ? <span>{(h as Link & { sub?: string | null }).sub}</span> : null}
              </span>
              <span className="go">{TYPE_LABEL[h.type]}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
